import { Button, Label, makeStyles } from '@fluentui/react-components'
import { useEffect, useState } from 'react'

import { playgroundsApi } from '../../../../../data/api/playgrounds.api'
import type { WorkspaceItem } from '../../../../../shared/models/WorkspaceItem'
import { useUploadPolicy } from '../../../../../shared/queries/useUploadPolicy'
import { resolveUploadFormat } from '../../../../../utils/fileWorksapceUploadUtil'

import {
  WorkspaceExplorerMode,
  WorkspaceExplorerSelectionMode,
} from './enums/WorkspaceExplorerMode'
import {
  useWorkspaceExplorer,
  WorkspaceExplorerProvider,
} from './providers/workspaceExplorerProvider'
import WorkspaceExplorerBreadcrumb from './WorkspaceExplorerBreadcrumb'
import WorkspaceExplorerItemsTable from './WorkspaceExplorerItemsTable'
import WorkspaceExplorerUpload from './WorkspaceExplorerUpload'

interface WorkspaceExplorerUploadProps {
  workspaceId: string
  parentId: string | null
  formId: string
  onSelectionChange: (hasSelection: boolean) => void
  onConfirm: (data: {
    workspaceId: string
    selectedItem?: WorkspaceItem
    statusRequest?: boolean
  }) => void
  onSelectionModeChange: (mode: WorkspaceExplorerSelectionMode | undefined) => void
}

const useStyles = makeStyles({
  backButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  backButton: {
    width: 'fit-content',
  },
  workspaceItemsForm: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  // root: {
  //   display: 'flex',
  //   flexDirection: 'column',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   gap: tokens.spacingVerticalS,
  //   minHeight: '180px',
  //   padding: tokens.spacingVerticalXL + ' ' + tokens.spacingHorizontalXL,
  //   border: tokens.strokeWidthThick + ' dashed ' + tokens.colorNeutralStroke1,
  //   // ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
  //   // ...shorthands.border(tokens.strokeWidthThick, 'dashed', tokens.colorNeutralStroke1),
  //   borderRadius: tokens.borderRadiusLarge,
  //   backgroundColor: tokens.colorNeutralBackground2,
  //   color: tokens.colorNeutralForeground2,
  //   cursor: 'pointer',
  //   transitionProperty: 'background-color, border-color',
  //   transitionDuration: tokens.durationFast,
  //   ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
  //   ':focus-visible': {
  //     outlineStyle: 'solid',
  //     outlineWidth: tokens.strokeWidthThick,
  //     outlineColor: tokens.colorStrokeFocus2,
  //   },
  // },
})

function WorkspaceFileSelector({
  formId,
  onUpload,
  onSelectionChange,
}: {
  formId: string
  onUpload: (item: WorkspaceItem) => void
  onSelectionChange: (hasSelection: boolean) => void
}) {
  const styles = useStyles()
  const { selectedItem, items, isLoading, isError } = useWorkspaceExplorer()
  const { data: policy } = useUploadPolicy('referenceImage')
  const selectedFile = items.find((item) => item.id === selectedItem?.id && item.type === 'file')
  const isSelectedFileAllowed =
    selectedFile !== undefined &&
    policy !== undefined &&
    selectedFile.sizeBytes !== null &&
    selectedFile.sizeBytes <= policy.maxFileSizeBytes &&
    resolveUploadFormat(selectedFile.name, selectedFile.mediaType, policy) !== null

  const hasSelection = !isLoading && !isError && isSelectedFileAllowed

  useEffect(() => {
    onSelectionChange(hasSelection)
  }, [hasSelection, onSelectionChange])
  const handlerSelect = (selectedFile: WorkspaceItem) => {
    onUpload(selectedFile)
  }

  return (
    <>
      <form
        className={styles.workspaceItemsForm}
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          if (isLoading || isError || !isSelectedFileAllowed) return

          handlerSelect(selectedFile)
        }}
      >
        <WorkspaceExplorerBreadcrumb />
        <WorkspaceExplorerItemsTable policyWorkflow="referenceImage" />
      </form>
    </>
  )
}

export default function WorkspaceExplorerUploadToPlayground({
  workspaceId,
  formId,
  onSelectionChange,
  onConfirm,
  onSelectionModeChange,
}: WorkspaceExplorerUploadProps) {

  const handleUpload = (item: WorkspaceItem) => {
    onConfirm({ workspaceId, selectedItem: item })
  }

  const [modeSelection, setModeSelection] = useState<WorkspaceExplorerSelectionMode | undefined>()

  const [uploadParentId, setUploadParentId] = useState<string | null>(null)

  useEffect(() => {
    onSelectionModeChange(modeSelection)
  }, [modeSelection, onSelectionModeChange])

  const handleSelectionMode = (mode: WorkspaceExplorerSelectionMode | undefined) => {
    setModeSelection(mode)
    if (mode === WorkspaceExplorerSelectionMode.UploadFile) {
      handleChooseUpload()
    }
  }

  const handleChooseUpload = async () => {
    try {
      const { data } = await playgroundsApi.referenceFolder(workspaceId)
      setUploadParentId(data.parentId)
      setModeSelection(WorkspaceExplorerSelectionMode.UploadFile)
    } catch {
      setModeSelection(undefined)
    }
  }

  return (
    <>
      <WorkspaceExplorerProvider
        key={workspaceId}
        workspaceId={workspaceId}
        mode={WorkspaceExplorerMode.Select}
      >
        {modeSelection === undefined && (
          <>
            <Button onClick={() => handleSelectionMode(WorkspaceExplorerSelectionMode.UploadFile)}>
              Upload File
            </Button>
            <Button
              onClick={() =>
                handleSelectionMode(WorkspaceExplorerSelectionMode.SelectFromWorkspace)
              }
            >
              Select from Workspace
            </Button>
          </>
        )}

        {modeSelection === WorkspaceExplorerSelectionMode.UploadFile && (
          <>
            {/* <div className={styles.backButtonContainer}>
              <Button
                className={styles.backButton}
                appearance="secondary"
                icon={<ArrowLeft24Regular />}
                onClick={() => handleSelectionMode(undefined)}
              >
                Back
              </Button>
            </div> */}
            <WorkspaceExplorerUpload
              workspaceId={workspaceId}
              parentId={uploadParentId}
              policyWorkflow="referenceImage"
              overrideMode={true}
              onConfirm={onConfirm}
            />
          </>
        )}

        {modeSelection === WorkspaceExplorerSelectionMode.SelectFromWorkspace && (
          <>
            {/* <div className={styles.backButtonContainer}>
              <Button
                className={styles.backButton}
                appearance="secondary"
                icon={<ArrowLeft24Regular />}
                onClick={() => handleSelectionMode(undefined)}
              >
                Back
              </Button>
            </div> */}
            <WorkspaceFileSelector
              formId={formId}
              onUpload={handleUpload}
              onSelectionChange={onSelectionChange}
            />
          </>
        )}
        {/* </div> */}
      </WorkspaceExplorerProvider>
    </>
  )
}
